import { getToolCategory } from '@/content/categories';
import type { Tool } from '@/content/tools';
import { accentClasses } from '@/lib/accent-colors';
import { iconMap } from '@/lib/icon-map';
import { cn } from '@/lib/utils';
import Link from 'next/link';

interface ToolCardProps {
  tool: Tool;
  className?: string;
}

export function ToolCard({ tool, className }: ToolCardProps) {
  const category = getToolCategory(tool.category);
  const Icon = iconMap[category.icon];
  const accent = accentClasses[category.accent];
  const isLive = tool.status === 'live';

  return (
    <Link
      href={`/tools/${tool.slug}`}
      data-slot="tool-card"
      className={cn(
        'group relative flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 shadow-xs transition-all hover:-translate-y-1 hover:shadow-lg',
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <div className={cn('flex size-10 items-center justify-center rounded-xl', accent.bg)}>
          <Icon className={cn('size-5', accent.text)} aria-hidden="true" />
        </div>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[11px] font-medium',
            isLive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
          )}
        >
          {isLive ? 'Live' : 'Coming soon'}
        </span>
      </div>
      <div>
        <h3 className="font-semibold text-foreground group-hover:text-primary">
          {tool.shortTitle ?? tool.title}
        </h3>
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{tool.description}</p>
      </div>
    </Link>
  );
}
